

























import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { websiteText as websiteTextFn } from '@/mixins/websiteTexts';

@Component
class StepNavigator extends Vue {
    @Prop() anchors!: {elementId: string, linkTitle: string}[];

    websiteText(text: string, extraValues?: string[]): string {
        return websiteTextFn(text, extraValues);
    }
    
    activeStep: number = 0;
    connectorProgress: number[] = [];
    isHeaderHidden: boolean = false;
    isStuck: boolean = false;
    lastScrollPosition: number = 0;

    topOffsets: { default: string; desktop: string; hidden: string } = (() => {
        const el = document.querySelector('.block-navigation');
        if (el) {
            const styles = getComputedStyle(el);
            return {
                default: styles.getPropertyValue("--_default-top"),
                desktop: styles.getPropertyValue("--_desktop-top"),
                hidden: styles.getPropertyValue("--_hidden-top")
            };
        }
        return { default: '110px', desktop: '75px', hidden: '50px' };
    })();

    @Watch('activeStep')
    onActiveStepChanged(newIndex: number) {
        this.scrollNavToStep(newIndex);
    }

    mounted() {
        this.connectorProgress = new Array(this.anchors.length - 1).fill(0);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.handleScroll();
    }

    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    scrollNavToStep(index: number) {
        const listElement = this.$refs.listRef as HTMLElement;
        if (!listElement) return;

        const stepElements = listElement.querySelectorAll('.step-item');
        const activeStepElement = stepElements[index] as HTMLElement;

        if (activeStepElement) {
            const containerRect = listElement.getBoundingClientRect();
            const elementRect = activeStepElement.getBoundingClientRect();
            
            const elementCenterOffset = elementRect.left - containerRect.left + (elementRect.width / 2);
            const scrollTarget = listElement.scrollLeft + elementCenterOffset - (containerRect.width / 2);
            
            listElement.scrollTo({
                left: scrollTarget,
                behavior: 'smooth'
            });
        }
    }

    getStickyOffset(scrollingDown?: boolean): number {
        if (window.matchMedia('(min-width: 1300px)').matches) {
            return 80;
        }
        if (scrollingDown !== undefined) {
            return scrollingDown ? 0 : 55;
        }
        return this.isHeaderHidden ? 0 : 55;
    }

    handleScroll() {
        const currentScroll = window.scrollY || document.documentElement.scrollTop;
        const isDesktop = window.matchMedia('(min-width: 1300px)').matches;

        if (!isDesktop) {
            if (currentScroll > this.lastScrollPosition && currentScroll > 55) {
                this.isHeaderHidden = true;
            } else if (currentScroll < this.lastScrollPosition) {
                this.isHeaderHidden = false;
            }
        } else {
            this.isHeaderHidden = false;
        }

        this.lastScrollPosition = currentScroll;

        const navElement = (this.$refs.navRef as HTMLElement).parentElement;
        
        if (navElement) {
            let expectedTop = parseInt(this.topOffsets.default, 10);
            if (isDesktop) {
                expectedTop = parseInt(this.topOffsets.desktop, 10);
            } else if (this.isHeaderHidden) {
                expectedTop = parseInt(this.topOffsets.hidden, 10);
            }
            
            this.isStuck = navElement.getBoundingClientRect().top <= (expectedTop + 1);
        }

        const stickyOffset = this.getStickyOffset();
        const navHeight = (navElement ? navElement.offsetHeight + 21 : 100) + stickyOffset; 

        let newActiveStep = 0;
        const newProgress = new Array(this.anchors.length - 1).fill(0);

        for (let i = 0; i < this.anchors.length; i++) {
            const currentEl = document.getElementById(this.anchors[i].elementId);
            if (!currentEl) continue;

            const currentTop = currentEl.getBoundingClientRect().top;

            if (currentTop <= navHeight) {
                newActiveStep = i;
            }

            if (i < this.anchors.length - 1) {
                const nextEl = document.getElementById(this.anchors[i + 1].elementId);
                if (nextEl) {
                    const nextTop = nextEl.getBoundingClientRect().top;
                    const totalDistance = nextTop - currentTop;
                    const scrolledDistance = navHeight - currentTop;

                    if (scrolledDistance <= 0) {
                        newProgress[i] = 0;
                    } else if (scrolledDistance >= totalDistance) {
                        newProgress[i] = 100;
                    } else {
                        const val = (scrolledDistance / totalDistance) * 100;
                        newProgress[i] = val < 4 ? 0 : val;
                    }
                }
            }
        }

        this.activeStep = newActiveStep;
        this.connectorProgress = newProgress;
    }

    scrollTo(id: string) {
        const element = document.getElementById(id);
        const navElement = (this.$refs.navRef as HTMLElement).parentElement;

        if (element && navElement) {
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const startTop = window.scrollY;
            const isScrollingDown = elementPosition > startTop;
            const predictedStickyOffset = this.getStickyOffset(isScrollingDown);
            const navHeight = navElement.offsetHeight;
            const activationThreshold = navHeight + 21 + predictedStickyOffset;
            const targetTop = elementPosition - activationThreshold + 5;
            const distance = targetTop - startTop;
            const duration = 250;
            const startTime = performance.now();

            const step = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const ease = progress < 0.5
                    ? 2 * progress * progress
                    : -1 + (4 - 2 * progress) * progress;
                window.scrollTo(0, startTop + distance * ease);
                if (progress < 1) requestAnimationFrame(step);
            };

            requestAnimationFrame(step);
        }
    }
}

export default StepNavigator;
